import type { CablePosition } from "../findCableGaps";
import type { KarlStepsResult } from "../karlSteps";

/**
 * Why this circuit holds the devices it holds. The estimator asked for the
 * reason in front of him — "even i cannot understand" was said of a queue that
 * asked about anomalies without ever showing the thing being questioned.
 */
export type RouteWhyKind =
  /** Its identifier is printed on the plan beside the devices. */
  | "key_on_plan"
  /** The schematic's schedule names this way; the key was matched to it. */
  | "key_from_schematic"
  /** No identifier was read: the devices were bagged by their symbol. */
  | "grouped_by_symbol"
  /** The board was read from the circuit id, nothing more. */
  | "board_only";

export interface RouteWhy {
  kind: RouteWhyKind;
  /** The way key as printed, when there is one. */
  key: string | null;
  /** The schematic board this way belongs to, when it was matched. */
  board: string | null;
  /** The breaker designator, for the schematic page lookup. */
  breaker: string | null;
  /** The schematic's cable id, a third name for the same way. */
  cableId: string | null;
  cableType: string;
  /** The run's own sentence, when it published one. Never invented. */
  note: string;
  /**
   * How many rows the schematic's schedule carries for THIS run's board.
   *
   * Zero is the answer to "why did you group these by symbol?", and it is a
   * different answer from "the schedule has rows but none of them names these".
   * On project 153 all 107 rows are under NSHV, and every one of the 32
   * symbol-grouped runs hangs off GHV, DV, BMZ or PAS/HES — boards whose
   * schedules were never in the package. There is nothing to cite for those
   * runs, and saying so is worth more than a vague reference: it tells him
   * which document to go and ask for.
   */
  scheduleRowsForBoard: number;
}

export interface RouteWalkItem {
  circuitId: string;
  /** What the estimator calls it — the printed key, else the circuit id. */
  label: string;
  /** Board it runs from. Empty when no feed point claimed it. */
  board: string;
  deviceCount: number;
  lengthM: number;
  cableType: string;
  why: RouteWhy;
  /** Every device on it, so the plan can frame the whole run. */
  positions: CablePosition[];
  /**
   * Anomalies the old grouping phase raised as separate questions. They belong
   * ON the route they are about, not standing alone in the queue.
   */
  flags: RouteFlag[];
}

export type RouteFlagKind = "stray_devices" | "unkeyed_bag" | "no_devices";

export interface RouteFlag {
  kind: RouteFlagKind;
  /** How many devices the flag is about. */
  devices: number;
  /** Where they sit, when the flag can point at them. */
  positions: CablePosition[];
  /**
   * WHICH devices, when the flag knows. Without these the follow-up could only
   * count them — "take them out for now" had nothing to take out, and said so
   * in its own comment.
   */
  destinationIds: string[];
}

export interface BuildRouteWalkArgs {
  steps: KarlStepsResult;
}

export interface BuildRouteWalkResult {
  /** Every circuit, in a stable order. This is "route 1 of 83". */
  routes: RouteWalkItem[];
  total: number;
}

export type BuildRouteWalk = (args: BuildRouteWalkArgs) => BuildRouteWalkResult;

export interface RouteWhyArgs {
  steps: KarlStepsResult;
  circuitId: string;
}

export type ReadRouteWhy = (args: RouteWhyArgs) => RouteWhy;

/** What the estimator has settled about one route, for the circuit list. */
export type RouteStatusKind = "untouched" | "confirmed" | "corrected";

export interface ReadRouteStatusArgs {
  /** Ask ids the queue has settled, with how they settled. */
  settled: Array<{ askId: string; outcome: string }>;
  circuitIds: string[];
}

export type ReadRouteStatus = (
  args: ReadRouteStatusArgs,
) => Record<string, RouteStatusKind>;

/** `route:<circuitId>` — the id shape both the asks and the status agree on. */
export const routeAskId = (circuitId: string): string => `route:${circuitId}`;

/** `route:group:<circuitId>` — the follow-up once he says some do not belong. */
export const routeGroupAskId = (circuitId: string): string =>
  `route:group:${circuitId}`;
